import React, { createContext, useContext, useEffect, useState } from 'react';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  return ctx;
};

function decodeJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (_) {
    return null;
  }
}

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const t = localStorage.getItem('admin_auth_token');
      const u = localStorage.getItem('admin_auth_user');
      if (t && u) {
        setAdminToken(t);
        setAdminUser(JSON.parse(u));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (user, token) => {
    // Ensure admin flag exists either on user or JWT
    let isAdmin = !!(user?.is_admin || user?.profile?.is_admin);
    if (!isAdmin && token) {
      const payload = decodeJwt(token);
      isAdmin = !!(payload && (payload.is_admin || payload.role === 'admin'));
    }
    if (!isAdmin) {
      throw new Error('Not authorized: admin access required');
    }
    setAdminUser(user);
    setAdminToken(token);
    localStorage.setItem('admin_auth_token', token);
    localStorage.setItem('admin_auth_user', JSON.stringify(user));
  };

  const logout = () => {
    setAdminUser(null);
    setAdminToken(null);
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_auth_user');
  };

  const isAuthenticated = () => !!adminUser && !!adminToken;

  return (
    <AdminAuthContext.Provider value={{ adminUser, adminToken, loading, login, logout, isAuthenticated }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
